import type {ReactElement, ReactNode} from 'react';
import React, {useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {AuthInput} from '@Components/auth-input';
import {EMAIL_PLACEHOLDER} from '@Libraries/login-signup-library';
import {INPUT_FIELD_TYPE} from '@Components/input-field';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {Checkbox} from '@Components/checkbox/checkbox';
import {CheckboxSize} from '@Components/checkbox/checkbox.types';
import type {VerifyUserEmailStateResponse} from '@Panels/connect-sso-account-panel/connect-sso-account-panel.types';
import {closePanel} from '@Components/panel/panel-reducer';
import {CONNECT_SSO_ACCOUNT_PANEL_ID} from '@Panels/connect-sso-account-panel';
import styles from './connect-account-email-required-form.module.scss';
import {useAppDispatch} from '@/hooks';

interface ConnectAccountEmailRequiredFormProps {
  onConnectCallback: (email: string) => void;
  showSubscribeToEmailsCheckbox?: boolean;
  onPasswordRequiredCallback: (email: string) => void;
}

export function ConnectAccountEmailRequiredForm({
  onConnectCallback,
  showSubscribeToEmailsCheckbox = false,
  onPasswordRequiredCallback,
}: ConnectAccountEmailRequiredFormProps): ReactElement {
  const [email, setEmail] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();

  const onSubmit = (): void => {
    if (email.length === 0) {
      setIsEmailInvalid(true);
      return;
    }

    void verifyUserEmailState();
  };

  const verifyUserEmailState = async (): Promise<void> => {
    setIsEmailInvalid(false);
    setIsLoading(true);

    try {
      const response = (await window.PMW.writeLocal('authenticate/verifyUserEmailState', {
        email,
        allowEmailMarketing: isChecked ? 1 : 0,
      })) as VerifyUserEmailStateResponse;

      if (response.state === 'allow') {
        onConnectCallback(email);
        dispatch(closePanel(CONNECT_SSO_ACCOUNT_PANEL_ID));
      } else if (response.state === 'ask-password') {
        dispatch(closePanel(CONNECT_SSO_ACCOUNT_PANEL_ID));
        onPasswordRequiredCallback(email);
      } else {
        setIsEmailInvalid(true);
      }
    } catch {
      setIsEmailInvalid(true);
    }

    setIsLoading(false);
  };

  const handleEmailInputChange = React.useCallback((txt: string) => {
    setEmail(txt.trim());
  }, []);

  const getEmailInputField = (): ReactElement => {
    return (
      <AuthInput
        enableFocusStyles
        errorText={window.i18next.t('pmwjs_valid_email_address')}
        handleInput={handleEmailInputChange}
        label={window.i18next.t('pmwjs_email')}
        onInputSubmit={onSubmit}
        placeholder={EMAIL_PLACEHOLDER}
        showError={isEmailInvalid}
        type={INPUT_FIELD_TYPE.EMAIL}
      />
    );
  };

  const getFreebieNotificationCheckbox = (): ReactNode => {
    if (!showSubscribeToEmailsCheckbox) {
      return null;
    }
    return (
      <label className={styles.notifyRow}>
        <Checkbox isChecked={isChecked} onChange={setIsChecked} size={CheckboxSize.SM} />
        <Text className={styles.notifyText} size={TextSize.MD} val={window.i18next.t('pmwjs_notify_promotions')} />
      </label>
    );
  };

  const getFormSubmitButton = (): ReactElement => {
    return <Button isFullWidth isLoading={isLoading} onClick={onSubmit} size={ButtonSize.MEDIUM} text={window.i18next.t('pmwjs_complete_signup_with_space')} type={ButtonType.PRIMARY} />;
  };

  return (
    <div className={styles.dialogBody}>
      <Text className={styles.description} size={TextSize.LG} val={window.i18next.t('pmwjs_enter_email_to_complete_signup')} />

      <div className={styles.formLayout}>
        {getEmailInputField()}

        <div className={styles.consentAndSubmit}>
          {getFreebieNotificationCheckbox()}
          {getFormSubmitButton()}
        </div>
      </div>
    </div>
  );
}
